/**
 * Toolbox page — installer-facing hub for calculators, useful info, and
 * cross-links to Marketing Materials + Video Library.
 *
 * Structure:
 *   Tab 1: Calculators   — grouped by trade (ProBound / ProEpoxy / ProDecor)
 *   Tab 2: Useful Info   — doc store (RA/MS, install guides, tech data)
 *                           plus cross-links to Marketing / Videos
 *
 * Visible to both installers and staff. Staff gets an extra upload button
 * in the docs section.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Calculator, FileStack, Megaphone, Video, Globe } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import ProPortalHeader from '@/components/ProPortalHeader';

import {
  BaseAsphaltCalc,
  BlockPavingCalc,
  CalculatorModal,
  MotType1Calc,
  PorousConcreteCalc,
  SelfSmoothingCalc,
  VentilationCalc,
} from '@/pages/toolbox/Calculators';
import DocumentsSection from '@/pages/toolbox/DocumentsSection';

const CALCULATORS = {
  probound: [
    { key: 'mot',      title: 'MOT Type 1 Calculator',      Comp: MotType1Calc,        blurb: 'Tonnes of sub-base for any drive.' },
    { key: 'asphalt',  title: 'Base Asphalt Calculator',    Comp: BaseAsphaltCalc,     blurb: 'Tonnes of base course tarmac.' },
    { key: 'porous',   title: 'Porous Concrete Calculator', Comp: PorousConcreteCalc,  blurb: 'Cubic metres of porous concrete.' },
    { key: 'block',    title: 'Block Paving Calculator',    Comp: BlockPavingCalc,     blurb: 'Edging runs or full-area block counts.' },
  ],
  proepoxy: [
    { key: 'self-smoothing', title: 'Self-Smoothing Compound', Comp: SelfSmoothingCalc, blurb: 'Bags needed for any floor prep.' },
    { key: 'ventilation',    title: 'Ventilation Calculator',   Comp: VentilationCalc,   blurb: 'Extractor rate for any room size.' },
  ],
  prodecor: [],
};

const BRAND_META = {
  probound: { label: 'ProBound', color: '#2F5E96', backPath: '/probound' },
  proepoxy: { label: 'ProEpoxy', color: '#7A0B7A', backPath: '/proepoxy' },
  prodecor: { label: 'ProDecor', color: '#D5AB09', backPath: '/prodecor' },
};

export default function Toolbox() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Scope by brand when opened from inside a sector's Hub page. Falls
  // back to "show everything" when hit standalone at /toolbox (e.g. staff
  // deep-link). Also filters the Documents feed to matching-brand docs.
  const rawBrand = (searchParams.get('brand') || '').toLowerCase();
  const scopedBrand = BRAND_META[rawBrand] ? rawBrand : null;
  const brandMeta = scopedBrand ? BRAND_META[scopedBrand] : null;

  const [tab, setTab] = useState('calculators');
  const [openCalc, setOpenCalc] = useState(null); // { title, Comp }

  const calcGroups = scopedBrand
    ? { [scopedBrand]: CALCULATORS[scopedBrand] || [] }
    : CALCULATORS;

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      <ProPortalHeader activeSection={scopedBrand || 'toolbox'} />

      <main className="max-w-6xl mx-auto px-4 py-6 md:py-10">
        <button
          onClick={() => navigate(brandMeta?.backPath || '/')}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-3"
          data-testid="toolbox-back"
        >
          <ArrowLeft className="w-4 h-4" /> {brandMeta ? `Back to ${brandMeta.label}` : 'Home'}
        </button>

        <div className="mb-6">
          {brandMeta && (
            <p className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: brandMeta.color }}>
              {brandMeta.label} · Toolbox
            </p>
          )}
          <h1 className="text-3xl font-bold text-[#27292E]">Calculators</h1>
          <p className="text-slate-600 mt-1">
            {brandMeta
              ? `Handy on-site calculators & reference documents for ${brandMeta.label} jobs.`
              : 'Handy calculators & reference documents for every job.'}
          </p>
        </div>

        {/* Tab picker */}
        <div className="inline-flex bg-white border rounded-xl p-1 mb-5" data-testid="toolbox-tabs">
          <button onClick={() => setTab('calculators')}
            className={`px-4 py-2 rounded-lg text-sm font-bold ${tab === 'calculators' ? 'bg-[#27292E] text-white' : 'text-slate-600'}`}
            data-testid="tab-calculators">
            <Calculator className="w-4 h-4 inline mr-1.5" /> Calculators
          </button>
          <button onClick={() => setTab('useful')}
            className={`px-4 py-2 rounded-lg text-sm font-bold ${tab === 'useful' ? 'bg-[#27292E] text-white' : 'text-slate-600'}`}
            data-testid="tab-useful">
            <FileStack className="w-4 h-4 inline mr-1.5" /> Useful Info
          </button>
        </div>

        {tab === 'calculators' && (
          <div className="space-y-6">
            {Object.entries(calcGroups).map(([brand, calcs]) => {
              if (!calcs.length) return null;
              const meta = BRAND_META[brand];
              // Hide the brand heading when we're already scoped to a
              // single brand — the page hero already shows it.
              const showHeading = !scopedBrand;
              return (
                <div key={brand}>
                  {showHeading && <h2 className="text-lg font-bold mb-2" style={{ color: meta.color }}>{meta.label}</h2>}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {calcs.map((c) => (
                      <Card key={c.key} className="p-4 cursor-pointer transition hover:shadow-md hover:border-slate-400"
                        onClick={() => setOpenCalc(c)} data-testid={`calc-${c.key}`}>
                        <Calculator className="w-6 h-6 mb-2" style={{ color: meta.color }} />
                        <h3 className="font-bold text-sm">{c.title}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{c.blurb}</p>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
            {scopedBrand && (calcGroups[scopedBrand] || []).length === 0 && (
              <Card className="p-8 text-center text-slate-500">
                No calculators added for {brandMeta.label} yet. Send Dan the formulas and they'll be here.
              </Card>
            )}
          </div>
        )}

        {tab === 'useful' && (
          <div className="space-y-6">
            {/* Cross-links to existing content — brand-scoped when possible */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card onClick={() => navigate('/toolbox/contact-form')}
                className="p-4 cursor-pointer transition hover:shadow-md hover:border-slate-400 flex items-center gap-3 md:col-span-2 border-[#006400]/40"
                data-testid="tile-contact-form">
                <div className="w-12 h-12 rounded-lg bg-[#006400]/10 text-[#006400] flex items-center justify-center shrink-0">
                  <Globe className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">Website Contact Form</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Get embed code for a contact form on your own site — enquiries land straight in your Lead CRM.</p>
                </div>
              </Card>
              <Card onClick={() => navigate(scopedBrand ? `/marketing-materials?section=${scopedBrand}` : '/marketing-materials')}
                className="p-4 cursor-pointer transition hover:shadow-md hover:border-slate-400 flex items-center gap-3"
                data-testid="tile-marketing">
                <div className="w-12 h-12 rounded-lg bg-[#7A0B7A]/10 text-[#7A0B7A] flex items-center justify-center shrink-0">
                  <Megaphone className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">Marketing Materials</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Flyers, social posts &amp; branded imagery.</p>
                </div>
              </Card>
              <Card onClick={() => navigate(scopedBrand ? `/videos/${scopedBrand}` : '/videos')}
                className="p-4 cursor-pointer transition hover:shadow-md hover:border-slate-400 flex items-center gap-3"
                data-testid="tile-videos">
                <div className="w-12 h-12 rounded-lg bg-[#2F5E96]/10 text-[#2F5E96] flex items-center justify-center shrink-0">
                  <Video className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">Video Library</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Install videos &amp; training clips.</p>
                </div>
              </Card>
            </div>

            <div>
              <h2 className="text-lg font-bold mb-2 text-slate-800">Documents</h2>
              <DocumentsSection initialBrand={scopedBrand || 'all'} lockBrand={!!scopedBrand} />
            </div>
          </div>
        )}
      </main>

      {openCalc && (
        <CalculatorModal open={!!openCalc} onClose={() => setOpenCalc(null)} title={openCalc.title}>
          <openCalc.Comp />
        </CalculatorModal>
      )}
    </div>
  );
}
